import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Radio,
  Divider,
} from "@material-ui/core";
import { Facebook as FacebookIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import {
  getFacebookConfig,
  listFacebookPages,
  connectFacebookPage,
} from "../../services/facebookOnboarding.api";
import toastError from "../../errors/toastError";
import loadFacebookSdk from "../../helpers/loadFacebookSdk";

// Permisos que el usuario otorga en el login (incluye pages_manage_metadata, el que faltaba en el
// alta manual). Con esto, el page token que devuelve /me/accounts SÍ puede suscribir el webhook.
const SCOPE =
  "pages_show_list,pages_messaging,pages_manage_metadata,pages_read_engagement,instagram_basic,instagram_manage_messages,business_management";

/**
 * Flujo OAuth "Iniciar sesión con Facebook" (0375 FB1-b): login → lista de páginas → elegir →
 * crea la conexión + suscribe el webhook automáticamente. Resuelve de raíz el problema de permisos
 * del alta manual (el usuario autoriza pages_manage_metadata en el propio login de Meta).
 */
export default function FacebookOAuthConnect({ provider = "facebook", onConnected }) {
  const isInstagram = provider === "instagram";
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [initError, setInitError] = useState(""); // motivo por el que el SDK no quedó listo
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(null); // null = no login aún; [] = login sin páginas
  const [userToken, setUserToken] = useState("");
  const [selectedPageId, setSelectedPageId] = useState("");
  const [connecting, setConnecting] = useState(false);

  // Prepara config + SDK. Extraído para poder "Reintentar" sin recargar la página.
  const init = useCallback(async () => {
    setInitError("");
    setSdkReady(false);
    try {
      const { data } = await getFacebookConfig();
      setConfig(data);
      await loadFacebookSdk(data.appId, data.graphVersion);
      setSdkReady(true);
    } catch (err) {
      const is404 = err?.response?.status === 404;
      setInitError(
        is404
          ? "El backend no responde /facebook-onboarding. Reinícialo para cargar la ruta nueva."
          : err?.message || "No se pudo preparar el acceso de Facebook."
      );
      console.warn("[FacebookOAuth] init falló:", err?.response?.status, err?.message);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  const handleLogin = useCallback(() => {
    if (!window.FB) {
      toast.error("El inicio de sesión con Facebook no está disponible: falta configurar la app de Meta en Configuración → Integraciones.");
      return;
    }
    setLoading(true);
    const opts = config?.configId
      ? { config_id: config.configId }
      : { scope: SCOPE, return_scopes: true };
    // OJO: el SDK de Facebook rechaza callbacks async ("Expression is of type asyncfunction,
    // not function"). El callback debe ser una función normal; la parte async va adentro.
    window.FB.login((response) => {
      (async () => {
        try {
          if (!response.authResponse) {
            setLoading(false);
            return; // usuario canceló
          }
          const token = response.authResponse.accessToken;
          const { data } = await listFacebookPages(token);
          setUserToken(data.userToken || token);
          setPages(data.pages || []);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      })();
    }, opts);
  }, [config]);

  const handleConnect = async () => {
    const page = (pages || []).find((p) => String(p.id) === String(selectedPageId));
    if (!page) {
      toast.error("Selecciona una página.");
      return;
    }
    if (isInstagram && !page.instagramAccountId) {
      toast.error("Esa página no tiene una cuenta de Instagram profesional vinculada.");
      return;
    }
    try {
      setConnecting(true);
      await connectFacebookPage({
        // Una misma página puede conectarse como Facebook Y como Instagram → nombres distintos
        // para no chocar con el UNIQUE de nombre por empresa (ERR_WAPP_DUP_NAME).
        name: isInstagram ? `${page.name} (Instagram)` : page.name,
        provider,
        pageId: page.id,
        pageName: page.name,
        pageAccessToken: page.accessToken,
        instagramAccountId: page.instagramAccountId || undefined,
        userToken,
      });
      toast.success("✅ Conexión creada y webhook suscrito. Escribe un mensaje de prueba para confirmar.");
      if (onConnected) onConnected();
    } catch (err) {
      toastError(err);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Box>
      {pages === null ? (
        <Box display="flex" flexDirection="column" alignItems="stretch" gap={1}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FacebookIcon />}
            onClick={handleLogin}
            disabled={loading || !sdkReady}
            style={{ backgroundColor: "#1877F2", color: "#fff", textTransform: "none", fontWeight: 600 }}
          >
            {loading ? "Conectando con Facebook…" : `Iniciar sesión con Facebook`}
          </Button>
          <Typography
            variant="caption"
            color={initError ? "error" : "textSecondary"}
            style={{ marginTop: 6 }}
          >
            {initError
              ? `⚠️ ${initError}`
              : sdkReady
              ? "Autoriza el acceso y elige la página. Los permisos se otorgan en el login (no hay que pegar tokens)."
              : "Preparando el acceso de Facebook…"}
          </Typography>
          {initError && (
            <Button
              size="small"
              onClick={init}
              style={{ marginTop: 6, alignSelf: "flex-start", textTransform: "none" }}
            >
              Reintentar
            </Button>
          )}
        </Box>
      ) : pages.length === 0 ? (
        <Typography color="error" variant="body2">
          Tu cuenta no administra ninguna página de Facebook. Crea o solicita acceso a una página y reintenta.
        </Typography>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Elige la {isInstagram ? "página con Instagram vinculado" : "página"} a conectar:
          </Typography>
          <List dense style={{ maxHeight: 220, overflowY: "auto" }}>
            {pages.map((p) => {
              const disabled = isInstagram && !p.instagramAccountId;
              return (
                <ListItem
                  key={p.id}
                  button
                  disabled={disabled}
                  onClick={() => setSelectedPageId(p.id)}
                  style={{ border: "1px solid #eee", borderRadius: 6, marginBottom: 6 }}
                >
                  <Radio checked={String(selectedPageId) === String(p.id)} color="primary" size="small" />
                  <ListItemText
                    primary={p.name}
                    secondary={
                      isInstagram
                        ? p.instagramAccountId
                          ? `IG: ${p.instagramAccountId}`
                          : "Sin Instagram vinculado"
                        : `Page ID: ${p.id}`
                    }
                  />
                </ListItem>
              );
            })}
          </List>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button onClick={() => { setPages(null); setSelectedPageId(""); }} disabled={connecting}>
              Volver
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConnect}
              disabled={connecting || !selectedPageId}
              startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {connecting ? "Conectando…" : "Conectar esta página"}
            </Button>
          </Box>
        </Box>
      )}
      <Divider style={{ margin: "16px 0" }} />
    </Box>
  );
}
